<script lang="ts">
  import { goto, yyyy, zzzz } from "../store.svelte";
  import Fa from "svelte-fa";
  import { faUpload } from "@fortawesome/free-solid-svg-icons";
  import { toast } from "../lib/toast.svelte.ts";

  const url = yyyy.uri.api;

  // Steps: 'select' → 'upload' → 'confirm' (or 'mismatch' on failure)
  let step = $state<"select" | "upload" | "confirm" | "mismatch">("select");
  let mismatchMessage = $state("");

  // Parent selection
  let selectedParent: any = $state(null);
  let availableLivewires = $derived(
    (yyyy.livewires || []).filter((lw: any) => lw.isready)
  );

  // Filter
  let filterText = $state("");
  let filteredLivewires = $derived(
    availableLivewires.filter((lw: any) =>
      lw.livewire.toLowerCase().includes(filterText.toLowerCase())
    )
  );

  // Upload state
  let file_upload: HTMLInputElement;
  let uploadProgress = $state(0);
  let filename = $state("");
  let uploading = $state(false);
  let serverPulling = $state(false);
  let checkingSchema = $state(false);
  let dragOver = $state(false);
  let selectingParent = $state(false);

  // Naming state
  let snapshotName = $state("");
  let liveName = $state("");
  let submitting = $state(false);

  // --- Step 1: Select parent and create new livewire ---

  const selectParent = async (lw: any) => {
    selectedParent = lw;
    selectingParent = true;

    // Suggest names
    const uploadDate = new Date(lw.created_at).toISOString().split("T")[0];
    snapshotName = `${lw.livewire} - ${uploadDate}`;
    liveName = lw.livewire;

    // Create new livewire using parent's SRID and tolerance
    const configResp = await fetch(
      `${url}/rpc/chronicle_get_parent_config`,
      {
        method: "post",
        body: JSON.stringify({ parent_wireident: lw.wireident }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + yyyy.sesh.logtoken.token,
        },
      }
    );

    if (!configResp.ok) {
      toast.error("Failed to read parent configuration");
      selectingParent = false;
      return;
    }

    const parentConfig = await configResp.json();
    yyyy.createNewLiveWire.parentConfig = parentConfig.config;

    // Extract SRID and tolerance from the config row
    const configRow = parentConfig.config.find((r: any) => r.tabletype === "config");
    const lw_srid = configRow?.tableconfig?.lw_srid;
    const lw_tolr = configRow?.tableconfig?.lw_tolerance;

    if (!lw_srid) {
      toast.error("Could not read SRID from parent config");
      selectingParent = false;
      return;
    }

    // Create the new livewire with a temporary name (will be renamed on confirm)
    const tempName = `__chronicle_${Date.now()}`;
    const createResp = await fetch(`${url}/rpc/create_livewire`, {
      method: "post",
      body: JSON.stringify({
        lw_name: tempName,
        lw_srid: lw_srid,
        lw_tolr: lw_tolr,
        lw_desc: `Chronicle update of ${lw.livewire}`,
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (createResp.ok) {
      const data = await createResp.json();
      yyyy.createNewLiveWire.data = data.info;
      selectingParent = false;
      step = "upload";
    } else {
      toast.error("Failed to create new livewire");
      selectingParent = false;
    }
  };

  // --- Step 2: Upload file, pull from S3, schema check ---

  const getSignedRequest = async (file: File) => {
    const response = await fetch(`${url}/rpc/create_livewire_get_url_for_upload`, {
      method: "post",
      body: JSON.stringify({ filename: file.name, filetype: file.type }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (response.ok) {
      const data = await response.json();
      uploadFile(file, data);
    } else {
      toast.error("Failed to get upload URL");
    }
  };

  const fileKickOff = () => {
    if (!file_upload.files?.length) return;
    startUpload(file_upload.files[0]);
  };

  const startUpload = (file: File) => {
    uploadProgress = 0.001;
    uploading = true;
    filename = file.name;
    getSignedRequest(file);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragOver = false;
    if (e.dataTransfer?.files?.length) {
      startUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    dragOver = true;
  };

  const handleDragLeave = () => {
    dragOver = false;
  };

  const uploadFile = (file: File, s3Data: any) => {
    const postData = new FormData();
    for (let key in s3Data.data.fields) {
      postData.append(key, s3Data.data.fields[key]);
    }
    postData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", s3Data.url);

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200 || xhr.status === 204) {
          onUploadComplete(s3Data.url);
        } else {
          toast.error("File could not be uploaded");
          uploading = false;
        }
      }
    };

    xhr.upload.addEventListener("progress", (e) => {
      uploadProgress = e.loaded / e.total;
    });

    xhr.send(postData);
  };

  const onUploadComplete = async (dlurl: string) => {
    serverPulling = true;

    // Pull file from S3 to server (reuses existing RPC)
    const pullResp = await fetch(`${url}/rpc/create_livewire_pull_file_from_space`, {
      method: "post",
      body: JSON.stringify({
        url: dlurl,
        wireident: yyyy.createNewLiveWire.data.wireident,
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (!pullResp.ok) {
      toast.error("Failed to process file on server");
      serverPulling = false;
      uploading = false;
      return;
    }

    let pullData = await pullResp.json();
    pullData = JSON.parse(pullData);
    yyyy.createNewLiveWire.data.ext_schema = pullData.ext_schema;
    yyyy.createNewLiveWire.layers = pullData.lyrs;

    serverPulling = false;
    checkingSchema = true;

    // Schema check: compare uploaded layers against parent config
    const schemaResp = await fetch(`${url}/rpc/chronicle_schema_check`, {
      method: "post",
      body: JSON.stringify({
        parent_wireident: selectedParent.wireident,
        upload_schema: yyyy.createNewLiveWire.data.ext_schema,
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    checkingSchema = false;
    uploading = false;

    if (schemaResp.ok) {
      const result = await schemaResp.json();
      if (result.compatible) {
        step = "confirm";
      } else {
        mismatchMessage = result.message;
        step = "mismatch";
      }
    } else {
      mismatchMessage = "Schema check failed unexpectedly";
      step = "mismatch";
    }
  };

  // --- Step 3: Submit ---

  const submitChronicle = async () => {
    submitting = true;

    // Check snapshot name doesn't already exist
    const nameResp = await fetch(`${url}/rpc/create_livewire_name_check`, {
      method: "post",
      body: JSON.stringify({ potential_name: snapshotName }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (!nameResp.ok) {
      const err = await nameResp.json();
      toast.error(err.details || err.message || "Snapshot name is not valid");
      submitting = false;
      return;
    }

    const response = await fetch(`${url}/rpc/chronicle_update`, {
      method: "post",
      body: JSON.stringify({
        parent_wireident: selectedParent.wireident,
        new_wireident: yyyy.createNewLiveWire.data.wireident,
        upload_schema: yyyy.createNewLiveWire.data.ext_schema,
        snapshot_name: snapshotName,
        live_name: liveName,
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (response.ok) {
      toast.success("Livewire updated! Generating...", undefined, "top-center", "vertical");
      setTimeout(() => {
        delete yyyy.createNewLiveWire;
        goto("/livewires");
      }, 2000);
    } else {
      const err = await response.json();
      toast.error(err.message || "Failed to update livewire");
      submitting = false;
    }
  };

  // --- Cleanup ---

  const deleteNewLivewire = async () => {
    if (yyyy.createNewLiveWire.data?.wireident) {
      await fetch(`${url}/livewires?wireident=eq.${yyyy.createNewLiveWire.data.wireident}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + yyyy.sesh.logtoken.token,
        },
      });
    }
  };

  const cancel = async () => {
    await deleteNewLivewire();
    delete yyyy.createNewLiveWire;
    goto("/livewires");
  };

</script>

<div class="chronicle-container">
  <div class="chronicle-card">
    <header class="chronicle-header">
      <h1 class="title">Update Existing Livewire</h1>
      <p class="subtitle">
        {#if step === "select"}
          Select the livewire to update
        {:else if step === "upload"}
          Upload new data for <strong>{selectedParent?.livewire}</strong>
        {:else if step === "mismatch"}
          Schema mismatch
        {:else}
          Confirm snapshot names
        {/if}
      </p>
    </header>

    <div class="chronicle-body">
      {#if step === "select"}
        <!-- Parent Livewire Selection -->
        <div class="parent-select">
          {#if availableLivewires.length === 0}
            <div class="no-livewires">
              <p>No livewires found.</p>
              <p class="hint">Create a livewire first, then come back to update it.</p>
            </div>
          {:else}
            <p class="select-hint">Select a livewire to update with new data. The new upload must have the same data structure.</p>
            <div class="filter-box">
              <input
                class="input"
                type="text"
                placeholder="Filter livewires..."
                bind:value={filterText}
              />
            </div>
            <div class="parent-list">
              {#each filteredLivewires as lw}
                <button
                  type="button"
                  class="parent-option button"
                  class:is-loading={selectingParent && selectedParent?.wireident === lw.wireident}
                  disabled={selectingParent}
                  onclick={() => selectParent(lw)}
                >
                  <span class="parent-name">{lw.livewire}</span>
                  <span class="parent-meta">
                    Created {new Date(lw.created_at).toLocaleDateString()}
                  </span>
                </button>
              {/each}
            </div>
          {/if}
        </div>

      {:else if step === "upload"}
        <!-- File Upload -->
        <div class="upload-section">
          {#if uploadProgress === 0}
            <div class="upload-prompt">
              <p class="upload-hint">Supported formats: GeoPackage, ESRI File Geodatabase, or zipped Shapefile</p>
              <button
                type="button"
                class="upload-btn"
                class:drag-over={dragOver}
                onclick={() => file_upload.click()}
                ondrop={handleDrop}
                ondragover={handleDragOver}
                ondragleave={handleDragLeave}
              >
                <Fa icon={faUpload} size="2x" />
                <span>Drag & drop your file here</span>
                <span class="upload-or">or click to browse</span>
              </button>
              <input
                bind:this={file_upload}
                type="file"
                class="hidden-input"
                onchange={fileKickOff}
              />
            </div>
          {:else}
            <div class="upload-progress">
              <h2>{Math.round(uploadProgress * 100)}%</h2>
              <progress class="progress is-primary" value={uploadProgress} max="1"></progress>
              <p class="filename">{filename}</p>
              {#if serverPulling}
                <p class="processing">Processing file on server...</p>
              {:else if checkingSchema}
                <p class="processing">Checking schema compatibility...</p>
              {/if}
            </div>
          {/if}
        </div>

      {:else if step === "mismatch"}
        <!-- Schema Mismatch -->
        <div class="mismatch-section">
          <div class="mismatch-warning">
            <p><strong>The uploaded file does not match the structure of {selectedParent?.livewire}.</strong></p>
            <p>The new data must have the same tables and columns as the existing livewire.</p>
          </div>
          <div class="mismatch-details">
            <p>{mismatchMessage}</p>
          </div>
          <p class="mismatch-hint">Check that you're uploading the correct file, or that the data export matches the original structure.</p>
        </div>

      {:else if step === "confirm"}
        <!-- Name Confirmation -->
        <div class="confirm-section">
          <div class="confirm-info">
            <p>The current version of <strong>{selectedParent?.livewire}</strong> will be archived as a snapshot, and the new upload will take its place.</p>
          </div>

          <div class="field">
            <label class="label" for="snapshot-name">Snapshot name (archived version)</label>
            <div class="control">
              <input
                id="snapshot-name"
                class="input"
                type="text"
                bind:value={snapshotName}
                placeholder="e.g. EE K21 - 2026-03-01"
              />
            </div>
            <p class="help">What the old version will be renamed to</p>
          </div>

          <div class="field">
            <label class="label" for="live-name">Live name (new version)</label>
            <div class="control">
              <input
                id="live-name"
                class="input"
                type="text"
                bind:value={liveName}
                placeholder="e.g. EE K21"
              />
            </div>
            <p class="help">The incoming livewire will use this name</p>
          </div>
        </div>
      {/if}
    </div>

    <footer class="chronicle-footer">
      {#if step === "mismatch"}
        <div></div>
        <button class="button is-danger" onclick={async () => { await deleteNewLivewire(); delete yyyy.createNewLiveWire; goto('/livewires'); }}>
          OK
        </button>
      {:else}
        <button class="button is-danger is-outlined" onclick={cancel} disabled={uploading || serverPulling || checkingSchema || submitting}>
          Cancel
        </button>
      {/if}

      {#if step === "confirm"}
        <button
          class="button is-primary"
          class:is-loading={submitting}
          disabled={!snapshotName.trim() || !liveName.trim()}
          onclick={submitChronicle}
        >
          Update Livewire
        </button>
      {/if}
    </footer>
  </div>
</div>

<style>
  .chronicle-container {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    width: 70%;
    max-width: 900px;
    height: fit-content;
    max-height: 90vh;
    padding: 1rem;
    z-index: 5;
  }

  .chronicle-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .chronicle-header {
    background: linear-gradient(135deg, #ff3e00 0%, #ff6b35 100%);
    color: white;
    padding: 1.5rem 2rem;
  }

  .chronicle-header .title {
    color: white;
    font-size: 1.5rem;
    font-weight: 300;
    text-transform: uppercase;
    margin-bottom: 0.25rem;
  }

  .chronicle-header .subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
  }

  .chronicle-body {
    padding: 2rem;
    min-height: 300px;
    max-height: 60vh;
    overflow-y: auto;
  }

  .chronicle-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 2rem;
    border-top: 1px solid #e0e0e0;
    background: #fafafa;
  }

  /* Parent selection */
  .select-hint {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .no-pinned {
    text-align: center;
    padding: 2rem;
    color: #666;
  }

  .no-pinned .hint {
    font-size: 0.85rem;
    color: #999;
    margin-top: 0.5rem;
  }

  .filter-box {
    margin-bottom: 1rem;
  }

  .parent-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .parent-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.25rem;
    background: #f9f9f9;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;
  }

  .parent-option:hover {
    background: #fff3e0;
    border-color: #ff3e00;
  }

  .parent-name {
    font-weight: 600;
    color: #333;
    font-size: 1rem;
  }

  .parent-option:hover .parent-name {
    color: #ff3e00;
  }

  .parent-meta {
    font-size: 0.8rem;
    color: #999;
  }

  /* Upload styles */
  .upload-section {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 250px;
    width: 100%;
  }

  .upload-prompt {
    text-align: center;
    width: 100%;
  }

  .upload-hint {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .upload-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 3rem;
    width: 100%;
    background: #f5f5f5;
    border: 2px dashed #ccc;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    color: #666;
  }

  .upload-btn:hover,
  .upload-btn.drag-over {
    background: #fff3e0;
    border-color: #ff3e00;
    color: #ff3e00;
  }

  .upload-or {
    font-size: 0.85rem;
    opacity: 0.7;
  }

  .hidden-input {
    display: none;
  }

  .upload-progress {
    text-align: center;
    width: 100%;
    max-width: 400px;
  }

  .upload-progress h2 {
    font-size: 3rem;
    font-weight: 200;
    color: #ff3e00;
    margin-bottom: 1rem;
  }

  .upload-progress .progress {
    height: 8px;
    margin-bottom: 1rem;
  }

  .filename {
    color: #666;
    font-size: 0.9rem;
  }

  .processing {
    color: #ff3e00;
    font-size: 0.9rem;
    margin-top: 1rem;
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  /* Mismatch styles */
  .mismatch-section {
    max-width: 600px;
    margin: 0 auto;
  }

  .mismatch-warning {
    margin-bottom: 1.25rem;
    padding: 1rem;
    background: #fff5f5;
    border-radius: 6px;
    border-left: 4px solid #f14668;
    color: #555;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .mismatch-details {
    padding: 1rem;
    background: #f5f5f5;
    border-radius: 6px;
    font-family: monospace;
    font-size: 0.85rem;
    color: #333;
    margin-bottom: 1.25rem;
    white-space: pre-wrap;
  }

  .mismatch-hint {
    font-size: 0.85rem;
    color: #999;
    font-style: italic;
  }

  /* Confirm styles */
  .confirm-section {
    max-width: 600px;
    margin: 0 auto;
  }

  .confirm-info {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f5f5f5;
    border-radius: 6px;
    border-left: 4px solid #ff3e00;
    font-size: 0.9rem;
    color: #555;
    line-height: 1.5;
  }

  .field {
    margin-bottom: 1.25rem;
  }

  .label {
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .help {
    font-size: 0.8rem;
    color: #999;
    margin-top: 0.25rem;
  }
</style>
